import React, { useState, useRef, useEffect } from 'react';
import { Input, Box, List, ListItem, useOutsideClick, Text } from 'components-lib';

/**
 * EnhancedAutocompleteInput Component
 * 
 * An improved autocomplete input that supports:
 * - API-based suggestions from doctor recommendations
 * - Custom user input (allows typing values not in suggestions)
 * - Keyboard navigation (up/down arrows, enter to select)
 * - Automatic suggestion filtering based on input
 * - Callback for when suggestions are selected vs manually typed
 */
const EnhancedAutocompleteInput = ({
  value = '',
  onChange,
  onSelect,
  suggestions = [],
  placeholder = '',
  allowCustomInput = true,
  maxSuggestions = 10,
  minInputLength = 1,
  caseSensitive = false,
  highlightMatch = true,
  onCustomInput,
  ...inputProps
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const ref = useRef();
  const inputRef = useRef();

  // Handle clicks outside the component
  useOutsideClick({
    ref: ref,
    handler: () => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    },
  });

  // Update local state when value prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  /**
   * Filter suggestions based on input value
   * Supports case-sensitive/insensitive matching
   */
  const getFilteredSuggestions = () => {
    if (!inputValue || inputValue.length < minInputLength) {
      return [];
    }

    const searchTerm = caseSensitive ? inputValue : inputValue.toLowerCase();
    
    return suggestions
      .filter(suggestion => {
        if (!suggestion) return false;
        const suggestionText = typeof suggestion === 'object'
          ? (suggestion.text || suggestion.advice || suggestion.name || String(suggestion))
          : String(suggestion);
        const searchAgainst = caseSensitive ? suggestionText : suggestionText.toLowerCase();
        return searchAgainst.includes(searchTerm);
      })
      .slice(0, maxSuggestions);
  };

  /**
   * Highlight matching text in suggestions
   */
  const highlightText = (text, highlight) => {
    if (!highlightMatch || !highlight) return text;
    
    const parts = String(text).split(new RegExp(`(${highlight})`, caseSensitive ? 'g' : 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === highlight.toLowerCase() ? (
        <Text as="span" key={index} bg="yellow.200" fontWeight="bold">
          {part}
        </Text>
      ) : part
    );
  };

  /**
   * Handle input value changes
   */
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSelectedIndex(-1);
    
    // Show suggestions if input meets minimum length requirement
    setShowSuggestions(newValue.length >= minInputLength);
    
    // Call onChange callback
    if (onChange) {
      onChange(e);
    }
  };

  /**
   * Handle suggestion selection
   */
  const handleSelect = (suggestion, isFromKeyboard = false) => {
    const isObject = typeof suggestion === 'object' && suggestion !== null;
    const selectedValue = isObject
      ? (suggestion.text || suggestion.advice || suggestion.name || String(suggestion))
      : String(suggestion);

    setInputValue(selectedValue);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    
    // Call onSelect callback if provided
    if (onSelect) {
      onSelect(selectedValue, {
        isFromSuggestions: true,
        isFromKeyboard,
        ...(isObject ? { metadata: suggestion } : {})
      });
    }
    
    // Focus back to input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = (e) => {
    const filteredSuggestions = getFilteredSuggestions();
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredSuggestions.length - 1
        );
        break;
        
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && filteredSuggestions[selectedIndex]) {
          handleSelect(filteredSuggestions[selectedIndex], true);
        } else if (allowCustomInput && inputValue.trim()) {
          // Handle custom input
          setShowSuggestions(false);
          if (onCustomInput) {
            onCustomInput(inputValue.trim());
          }
          if (onSelect) {
            onSelect(inputValue.trim(), { isFromSuggestions: false, isCustomInput: true });
          }
        }
        break;
        
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
        
      default:
        break;
    }
  };

  /**
   * Handle input focus
   */
  const handleFocus = () => {
    if (inputValue.length >= minInputLength) {
      setShowSuggestions(true);
    }
  };

  /**
   * Handle input blur
   */
  const handleBlur = () => {
    // Small delay to allow for suggestion clicks
    setTimeout(() => {
      if (allowCustomInput && inputValue.trim() && onCustomInput) {
        onCustomInput(inputValue.trim());
      }
    }, 150);
  };

  const filteredSuggestions = getFilteredSuggestions();

  return (
    <Box position="relative" ref={ref} width="100%">
      <Input
        ref={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        autoComplete="off"
        {...inputProps}
      />
      
      {showSuggestions && filteredSuggestions.length > 0 && (
        <Box
          position="absolute"
          zIndex="dropdown"
          width="100%"
          maxH="200px"
          overflowY="auto"
          bg="white"
          boxShadow="lg"
          borderRadius="md"
          mt={1}
          border="1px solid"
          borderColor="gray.200"
        >
          <List spacing={0} p={0}>
            {filteredSuggestions.map((suggestion, index) => (
              <ListItem
                key={index}
                p={3}
                bg={selectedIndex === index ? 'blue.50' : 'white'}
                borderBottom={index < filteredSuggestions.length - 1 ? '1px solid' : 'none'}
                borderColor="gray.100"
                _hover={{ 
                  bg: selectedIndex === index ? 'blue.100' : 'gray.50',
                  cursor: 'pointer' 
                }}
                onClick={() => handleSelect(suggestion)}
                fontSize="sm"
              >
                {highlightText(
                  typeof suggestion === 'object'
                    ? (suggestion.text || suggestion.advice || suggestion.name || String(suggestion))
                    : suggestion,
                  inputValue
                )}
              </ListItem>
            ))}
          </List>
        </Box>
      )}
      
      {/* Show "Press Enter to add custom" hint when no suggestions match */}
      {showSuggestions && filteredSuggestions.length === 0 && allowCustomInput && inputValue.trim() && (
        <Box
          position="absolute"
          zIndex="dropdown"
          width="100%"
          bg="gray.50"
          borderRadius="md"
          mt={1}
          border="1px solid"
          borderColor="gray.200"
          p={3}
        >
          <Text fontSize="sm" color="gray.600" fontStyle="italic">
            Press Enter to add "{inputValue}"
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default EnhancedAutocompleteInput;